
import { defineComponent, PropType } from "vue";
import SingleSelect from "@/modules/reports/controllers/SingleSelect.vue";
import { Input } from "@progress/kendo-vue-inputs";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { mapGetters, mapActions, mapState } from "vuex";
import { reportsActions } from "@/store/reports/types";
import { useI18n } from "vue-i18n";
import { RootGetters } from "@/store/getters";
import IBMultiSelectCaseReport from "./IBMultiSelectCaseReport.vue";
import IBDropdownCaseReport from "./IBDropdownCaseReport.vue";

export interface Controller {
  type: string;
  options?: Array<Option>;
  isFilterable?: boolean;
  inputType: string;
  required: boolean;
  min?: number;
  max?: number;
  step?: number;
}

export interface Option {
  text: string;
  value: string;
}

export default defineComponent({
  name: "CaseReportObservation",
  setup() {
    const { t } = useI18n({
      inheritLocale: true,
      useScope: "local",
    });
    return { t };
  },
  props: {
    caseReportObservations: {
      type: Array,
      default: [],
    },
    observationKey: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: "",
    },
    controller: {
      type: Object as PropType<Controller>,
      required: true,
    },
    value: {
      type: [String, Array<String>],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(["insightsConfig"]),
    ...mapGetters({
      activeCase: RootGetters.GET_ACTIVE_CASE,
    }),
    inputValue: {
      get(): string | Array<string> | number {
        if (this.value) {
          return this.value;
        } else if (this.controller.type === "multi") {
          return [];
        } else {
          return "";
        }
      },
      async set(value: string | Array<string> | number) {
        await this.updateCaseObservation({
          observationKey: this.observationKey,
          observationTitle: this.t(
            `config.insight.case.${this.activeCase.tissue}.${this.observationKey}.title`
          ),
          observationValue: value,
        });
        this.$emit("observationChange");
      },
    },
    isHidden() {
      // make sure not to trigger the logic when moving to the cases list
      if (!this.activeCase) return false;

      // don't hide if there's no show/hide configuration for this insight
      if (!this.controller.target_observation) {
        return false;
      } else {
        const watchedObservation = this.caseReportObservations?.find(
          (observation) => observation.key === this.controller.target_observation
        );
        let targetObservationConfig = this.insightsConfig.case_insights[
          this.activeCase.tissue
        ].find((config) => config.key === watchedObservation.key);

        // we have to test the translated strings and not the enums...
        let translatedTargetValues = [];
        this.controller.watch_target_values.map((value) => {
          let translateValue;
          if (targetObservationConfig?.controller.type === "input") {
            //no need to translate
            translateValue = value;
          } else {
            translateValue = this.t(
              `config.insight.case.${this.activeCase.tissue}.${watchedObservation.key}.${value}`
            );
          }
          translatedTargetValues.push(translateValue);
        });
        let isRuleActive;
        if (watchedObservation.value instanceof Array) {
          isRuleActive = translatedTargetValues.some((r) => watchedObservation.value.includes(r));
        } else {
          isRuleActive = translatedTargetValues.includes(watchedObservation.value);
        }
        if (this.controller.watch_rule === "show") {
          return isRuleActive ? false : true;
        } else {
          return isRuleActive ? true : false;
        }
      }
    },
    translatedOptions(): Array<string> {
      return this.controller.options.map((option: string) => {
        return this.t(
          `config.insight.case.${this.activeCase.tissue}.${this.observationKey}.${option}`
        );
      });
    },
    insightConfigSize(): string {
      const size = this.insightsConfig.case_insights[this.activeCase.tissue].find(
        (config) => config.key === this.observationKey
      ).controller.size;
      return size || "single";
    },
    isBreakAfter(): boolean {
      return this.insightsConfig.case_insights[this.activeCase.tissue].find(
        (config) => config.key === this.observationKey
      ).controller.break_after;
    },
    isEmptyValue(): boolean {
      switch(typeof(this.inputValue)) {
        case "number":
          return false;
        case "string":
        case "object":
          return this.inputValue.length === 0
        default:
          return true;
      }
    }
  },
  watch: {
    isHidden(val) {
      if (val === true) {
        if (this.controller.type === "multi") {
          this.inputValue = [];
        } else {
          this.inputValue = "";
        }
      }
    },
  },
  components: {
    SingleSelect,
    "k-input": Input,
    "k-drop-down-list": DropDownList,
    IBMultiSelectCaseReport,
    IBDropdownCaseReport,
  },
  methods: {
    ...mapActions("reportsModule", {
      updateCaseObservation: reportsActions.UPDATE_CASE_OBSERVATION,
    }),
  },
});
